import { TFunction } from 'i18next';

/**
 * Utility to convert a string to camelCase for translation key generation
 */
export function toCamelCase(str: string): string {
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
        .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
}

/**
 * Generate a translation key from a product/category name
 */
export function generateTranslationKey(name: string, type: 'title' | 'description' = 'title'): string {
    const camelName = toCamelCase(name);
    return `dynamicContent.${camelName}.${type}`;
}

/**
 * Translation cache to improve performance
 */
const translationCache = new Map<string, Map<string, string>>();

/**
 * Clear translation cache (useful when language changes)
 */
export function clearTranslationCache(): void {
    translationCache.clear();
}

/**
 * Get cached translation or compute and cache it
 */
function getCachedTranslation(
    key: string,
    language: string,
    t: TFunction,
    fallback: string
): string {
    if (!translationCache.has(language)) {
        translationCache.set(language, new Map());
    }

    const langCache = translationCache.get(language)!;

    if (langCache.has(key)) {
        return langCache.get(key)!;
    }

    const translated = t(key);
    const result = translated !== key ? translated : fallback;
    langCache.set(key, result);

    return result;
}

/**
 * Translate a single field value
 */
export function translateField(
    value: string,
    fieldName: string,
    t: TFunction,
    language: string
): string {
    if (!value || typeof value !== 'string') {
        return value;
    }

    // Determine the type based on field name
    const type = fieldName.toLowerCase().includes('desc') ? 'description' : 'title';
    const key = generateTranslationKey(value, type);

    return getCachedTranslation(key, language, t, value);
}

/**
 * Fields that should be translated in backend data
 */
const TRANSLATABLE_FIELDS = [
    'name',
    'categoryName',
    'title',
    'description',
    'desc',
    'shortDescription',
    'briefDescription',
    'label',
    'text',
    'heading',
    'subheading',
    'subtitle'
];

/**
 * Check if a field should be translated
 */
function isTranslatableField(fieldName: string): boolean {
    return TRANSLATABLE_FIELDS.some(field =>
        fieldName.toLowerCase() === field.toLowerCase()
    );
}

/**
 * Recursively translate all translatable fields in an object
 */
export function translateObject<T>(
    obj: T,
    t: TFunction,
    language: string,
    depth: number = 0
): T {
    // Prevent infinite recursion
    if (depth > 10) {
        return obj;
    }

    if (!obj || typeof obj !== 'object') {
        return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
        return obj.map(item => translateObject(item, t, language, depth + 1)) as unknown as T;
    }

    // Handle objects
    const translated: any = { ...obj };

    for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'string' && isTranslatableField(key)) {
            const translatedValue = translateField(value, key, t, language);
            console.log(`[translate-middleware] field="${key}" original="${value}" translated="${translatedValue}"`);
            translated[key] = translatedValue;
        } else if (typeof value === 'object' && value !== null) {
            translated[key] = translateObject(value, t, language, depth + 1);
        } else {
            translated[key] = value;
        }
    }

    return translated;
}

/**
 * Main translation middleware function
 * Automatically translates all backend data based on current language
 */
export function translateBackendData<T>(
    data: T,
    t: TFunction,
    language: string
): T {
    return translateObject(data, t, language);
}

/**
 * Batch translate multiple items (useful for lists)
 */
export function translateBackendDataBatch<T>(
    items: T[],
    t: TFunction,
    language: string
): T[] {
    return items.map(item => translateBackendData(item, t, language));
}

/**
 * Get translation with fallback chain
 * Tries: translation key -> shortDescription -> briefDescription -> original value
 */
export function getTranslationWithFallback(
    primaryValue: string,
    fallbackValues: (string | undefined)[],
    t: TFunction,
    language: string
): string {
    // Try primary translation
    const key = generateTranslationKey(primaryValue, 'description');
    const translated = getCachedTranslation(key, language, t, '');

    if (translated && translated !== key) {
        return translated;
    }

    // Try fallback values
    for (const fallback of fallbackValues) {
        if (fallback && fallback.trim()) {
            return fallback;
        }
    }

    // Return original value
    return primaryValue;
}
